import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCheck, Download, Plus, Search, Trash2, X } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useApi, errMsg, downloadBlob } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { isStaff, canViewAll, categoryLabel, JET_PROGRAMS, fmtDate, money } from "@/lib/format";
import { PageHero } from "@/components/PageHero";
import JetCardForm, { JetCardThanks } from "@/components/JetCardForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SORTS = {
  newest: ["Newest first", (a, b) => b.created_at.localeCompare(a.created_at)],
  oldest: ["Oldest first", (a, b) => a.created_at.localeCompare(b.created_at)],
  hours: ["Most hours", (a, b) => (b.hours || 0) - (a.hours || 0)],
  name: ["Client name A–Z", (a, b) => (a.full_name || "").localeCompare(b.full_name || "")],
};
const ALL = { q: "", status: "all", broker: "all", program: "all", sort: "newest" };

function applyFilters(list, f) {
  const q = f.q.trim().toLowerCase();
  return list
    .filter((r) => f.status === "all" || r.status === f.status)
    .filter((r) => f.broker === "all" || r.broker_name === f.broker)
    .filter((r) => f.program === "all" || r.program === f.program)
    .filter((r) => !q || [r.ref, r.full_name, r.company, r.email, r.broker_name].some((v) => (v || "").toLowerCase().includes(q)))
    .sort(SORTS[f.sort][1]);
}

function Pick({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-[#0d0f14] border-white/10 sm:w-48" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map(([v, label]) => <SelectItem key={v} value={v} data-testid={`${testId}-${v}`}>{label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function JetCardFilters({ f, setF, list, staff }) {
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const brokers = [...new Set(list.map((r) => r.broker_name).filter(Boolean))].sort();
  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6 max-w-4xl" data-testid="jc-filters">
      <div className="relative sm:w-64">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search client, ref or email" className="pl-9 bg-[#0d0f14] border-white/10" data-testid="jc-filter-search" />
      </div>
      <Pick value={f.status} onChange={set("status")} testId="jc-filter-status" options={[["all", "All statuses"], ["new", "Awaiting acknowledgement"], ["acknowledged", "Received"]]} />
      {staff && <Pick value={f.broker} onChange={set("broker")} testId="jc-filter-broker" options={[["all", "All brokers"], ...brokers.map((b) => [b, b])]} />}
      <Pick value={f.program} onChange={set("program")} testId="jc-filter-program" options={[["all", "All programs"], ...Object.entries(JET_PROGRAMS).map(([k, p]) => [k, p.label])]} />
      <Pick value={f.sort} onChange={set("sort")} testId="jc-filter-sort" options={Object.entries(SORTS).map(([k, [label]]) => [k, label])} />
      {JSON.stringify(f) !== JSON.stringify(ALL) && (
        <button type="button" className="frame-btn" onClick={() => setF(ALL)} data-testid="jc-filter-clear"><X size={13} /> Clear</button>
      )}
    </div>
  );
}

function AckDialog({ req, onClose, onSaved }) {
  const api = useApi();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post(`/jet-card-requests/${req.id}/acknowledge`, { note }); toast.success("Acknowledged. The broker has been emailed."); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#111319] border-white/10" data-testid="jc-ack-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl font-normal">Acknowledge {req?.ref}</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-400">The broker will be emailed that the request was received and the DocuSign is being prepared.</p>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="Optional note to the broker…" className="bg-[#0d0f14] border-white/10" data-testid="jc-ack-note-input" />
        <button onClick={save} disabled={saving} className="frame-btn gold self-end" data-testid="jc-ack-confirm-btn"><CheckCheck size={14} /> {saving ? "Saving…" : "Acknowledge receipt"}</button>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRequest({ r, onDeleted }) {
  const api = useApi();
  const del = () => api.delete(`/jet-card-requests/${r.id}`).then(() => { toast.success(`${r.ref} deleted`); onDeleted(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="frame-btn text-rose-400" data-testid={`jc-request-${r.ref}-delete-btn`}><Trash2 size={14} /> Delete</button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#111319] border-white/10" data-testid="jc-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif-display text-2xl font-normal">Delete {r.ref}?</AlertDialogTitle>
          <AlertDialogDescription>The Jet Card request for {r.full_name} and its contract are removed for you and the broker. This can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="jc-delete-cancel-btn">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-500 text-white" data-testid="jc-delete-confirm-btn">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RequestCard({ r, staff, viewAll, isAdmin, onAck, onDeleted }) {
  const api = useApi();
  const acked = r.status === "acknowledged";
  const adminOnly = r.custom_deal && !isAdmin;
  return (
    <article className={`lux-card m-card p-5 ${acked ? "" : "!border-[#C6A55C]/60"}`} data-testid={`jc-request-${r.ref}`}>
      <div className="flex flex-wrap items-start gap-3 justify-between">
        <div>
          <p className="font-mono-data text-[11px] text-[#5B7FD1]">{r.ref} · {fmtDate(r.created_at)}{viewAll ? ` · ${r.broker_name}` : ""}{r.created_by_name ? ` · added by ${r.created_by_name}` : ""}</p>
          <h3 className="font-serif-display text-2xl text-white mt-1">{r.titled_to === "company" ? r.company : r.full_name}</h3>
          <p className="text-sm text-zinc-400 mt-1">{r.hours} hours · {JET_PROGRAMS[r.program]?.label} · {categoryLabel(r.category)}{r.price ? ` · ${money(r.price)}` : ""}{r.hourly_rate ? ` (${money(r.hourly_rate)}/hr)` : ""}</p>
          {r.custom_deal && (
            <p className="text-xs text-[#8A6D2B] mt-1" data-testid={`jc-request-${r.ref}-custom-deal`}>
              {r.pricing === "bonus_hours" ? `Custom deal: ${r.hours} hours for the price of ${r.paid_hours}` : "Custom price"} · standard {money(r.standard_price)}
            </p>
          )}
        </div>
        <span className={`px-2.5 py-1 text-[0.6rem] tracking-[0.18em] uppercase ${acked ? "bg-emerald-700 !text-white" : "bg-[#C6A55C] text-black font-semibold"}`} data-testid={`jc-request-${r.ref}-status`}>
          {acked ? "Received" : "Awaiting acknowledgement"}
        </span>
      </div>
      <p className="text-xs text-zinc-500 mt-3">{r.full_name} · {r.email} · {r.phone}{r.company ? ` · ${r.company}` : ""}</p>
      {r.extra_terms && <p className="text-sm text-zinc-300 mt-3 whitespace-pre-line">{r.extra_terms}</p>}
      {acked && <p className="text-xs text-emerald-800 mt-3">Acknowledged by {r.acknowledged_by_name || "the team"} · {fmtDate(r.acknowledged_at)}{r.acknowledged_note ? ` · ${r.acknowledged_note}` : ""}</p>}
      {!viewAll && !acked && <p className="text-xs text-zinc-500 mt-3" data-testid={`jc-request-${r.ref}-pdf-hint`}>You can download the filled contract once the team acknowledges this request.</p>}
      {(viewAll || acked) && (
        <div className="flex flex-wrap gap-2 mt-4">
          {staff && !acked && (adminOnly
            ? <span className="text-xs text-zinc-500 self-center" data-testid={`jc-request-${r.ref}-admin-only`}>Custom deal · only an admin can acknowledge</span>
            : <button onClick={() => onAck(r)} className="frame-btn gold" data-testid={`jc-request-${r.ref}-ack-btn`}><CheckCheck size={14} /> Acknowledge</button>)}
          <button onClick={() => downloadBlob(api, `/jet-card-requests/${r.id}/pdf`, `JETSETGRP-Jet-Card-Agreement-${r.ref}.pdf`).catch((e) => toast.error(errMsg(e)))}
            className="frame-btn" data-testid={`jc-request-${r.ref}-pdf-btn`}><Download size={14} /> Download contract</button>
          {staff && <DeleteRequest r={r} onDeleted={onDeleted} />}
        </div>
      )}
    </article>
  );
}

export default function JetCardRequests() {
  const api = useApi();
  const { me } = useMe();
  const staff = isStaff(me);
  const viewAll = canViewAll(me);
  const [list, setList] = useState(null);
  const [done, setDone] = useState(false);
  const [ack, setAck] = useState(null);
  const isAdmin = me.role === "admin";
  const [adding, setAdding] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [f, setF] = useState(ALL);
  const shown = useMemo(() => applyFilters(list || [], f), [list, f]);
  const load = useCallback(() => api.get("/jet-card-requests").then((r) => setList(r.data)).catch(() => setList([])), [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAdmin) api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => {});
  }, [api, isAdmin]);

  return (
    <div data-testid="jet-cards-page">
      <PageHero testId="jet-cards-hero">
        <p className="eyebrow mb-3">{staff ? "Flight Department Desk" : "Jet Card Program"}</p>
        <h1 className="font-serif-display text-[2rem] sm:text-4xl lg:text-5xl text-white leading-[1.12] max-lg:uppercase">{staff ? "Jet Card Requests" : "Request a Jet Card Agreement"}</h1>
        <p className="text-zinc-300 text-sm mt-4 max-w-xl">{staff ? "Agreement requests from brokers. Acknowledge receipt and download the details for the DocuSign contract." : "Send your client's details and we'll prepare the Jet Card agreement for DocuSign."}</p>
      </PageHero>
      {!staff && (done ? <JetCardThanks onAgain={() => setDone(false)} /> : <JetCardForm onSubmitted={() => { setDone(true); load(); }} />)}
      {isAdmin && (
        <div className="mb-10" data-testid="jc-admin-add">
          <button type="button" className={`frame-btn ${adding ? "" : "gold"} mb-6`} onClick={() => setAdding((a) => !a)} data-testid="jc-admin-add-btn">
            {adding ? <><X size={14} /> Close form</> : <><Plus size={14} /> Add Jet Card request</>}
          </button>
          {adding && <JetCardForm brokers={brokers} onSubmitted={(r) => { toast.success(`${r.ref} added for ${r.broker_name}. The broker has been notified.`); setAdding(false); load(); }} />}
        </div>
      )}
      <section className={staff ? "" : "mt-14"}>
        {!staff && <p className="eyebrow mb-4">{viewAll ? "All Jet Card requests (view only)" : "Your requests"}</p>}
        {list?.length > 0 && <JetCardFilters f={f} setF={setF} list={list} staff={viewAll} />}
        <div className="grid gap-4 max-w-4xl" data-testid="jc-request-list">
          {shown.map((r) => <RequestCard key={r.id} r={r} staff={staff} viewAll={viewAll} isAdmin={isAdmin} onAck={setAck} onDeleted={load} />)}
          {list?.length > 0 && shown.length === 0 && <p className="text-sm text-zinc-500" data-testid="jc-filter-empty">No requests match these filters.</p>}
          {list?.length === 0 && <p className="text-sm text-zinc-500" data-testid="jc-request-empty">{staff ? "No Jet Card requests yet." : "You haven't requested a Jet Card agreement yet."}</p>}
        </div>
      </section>
      <AckDialog req={ack} onClose={() => setAck(null)} onSaved={load} />
    </div>
  );
}
